import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { format, isSameDay } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import {
  CalendarDays,
  CheckSquare,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import scheduleService from '../../services/scheduleService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { FLAT_SCRAP_REASONS, findScrapReason } from '../../constants/scrapReasons';

function DailySettleDialog({ open, onClose, dates, schedules, onConfirm, initialDate, singleDay = false }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (singleDay && initialDate) {
      return initialDate;
    }
    if (initialDate && dates.some(d => isSameDay(d, initialDate))) {
      return initialDate;
    }
    return dates[0] || new Date();
  });
  const [loading, setLoading] = useState(false);
  const [itemInputs, setItemInputs] = useState({});

  // Sync selectedDate when dialog opens with a new initialDate
  useEffect(() => {
    if (!open) return;
    if (singleDay && initialDate) {
      setSelectedDate(initialDate);
      setItemInputs({});
    } else if (initialDate && dates.some(d => isSameDay(d, initialDate))) {
      setSelectedDate(initialDate);
      setItemInputs({});
    } else if (dates.length > 0) {
      setSelectedDate(dates[0]);
      setItemInputs({});
    }
  }, [open, singleDay, initialDate, dates]);

  // When settling a historical date outside the current week, fetch its schedules
  const isHistoricalDate = singleDay && initialDate && !dates.some(d => isSameDay(d, initialDate));
  const [historicalSchedules, setHistoricalSchedules] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);

  useEffect(() => {
    if (!open || !isHistoricalDate || !initialDate) {
      setHistoricalSchedules([]);
      return;
    }

    let cancelled = false;
    setHistoricalLoading(true);
    const dateStr = format(initialDate, 'yyyy-MM-dd');
    scheduleService.getSchedules({ start_date: dateStr, end_date: dateStr })
      .then((data) => {
        if (!cancelled) {
          setHistoricalSchedules(data?.schedules || []);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch historical schedules:', err);
        if (!cancelled) setHistoricalSchedules([]);
      })
      .finally(() => {
        if (!cancelled) setHistoricalLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, isHistoricalDate, initialDate]);

  const effectiveSchedules = useMemo(() => {
    if (isHistoricalDate) {
      return historicalSchedules;
    }
    return schedules;
  }, [isHistoricalDate, historicalSchedules, schedules]);

  const settleData = useMemo(() => {
    const datesToRender = [...dates];
    // In single-day mode, ensure initialDate is included even if it's outside the current week
    if (singleDay && initialDate && !datesToRender.some(d => isSameDay(d, initialDate))) {
      datesToRender.push(initialDate);
    }

    const result = datesToRender.map((date) => {
      const daySchedules = effectiveSchedules.filter((s) => {
        const schDate = new Date(s.scheduled_date);
        return isSameDay(schDate, date);
      });
      const uncompleted = daySchedules.filter(
        (s) => !['completed', 'rolled_over', 'cancelled'].includes(s.status)
      );
      const completed = daySchedules.filter((s) => s.status === 'completed');
      return {
        date,
        total: daySchedules.length,
        uncompleted: uncompleted.length,
        completed: completed.length,
        uncompletedItems: uncompleted,
      };
    });
    return result;
  }, [dates, effectiveSchedules, singleDay, initialDate]);

  const selectedDay = settleData.find((d) => isSameDay(d.date, selectedDate));

  // Reset inputs when date changes
  const handleSelectDate = useCallback((date) => {
    setSelectedDate(date);
    setItemInputs({});
  }, []);

  const handleInputChange = (scheduleId, field, value) => {
    setItemInputs((prev) => {
      const updated = {
        ...prev,
        [scheduleId]: {
          ...prev[scheduleId],
          [field]: value,
        },
      };
      // Clear scrap reasons if scrapped quantity becomes 0
      if (field === 'scrapped_quantity' && (!value || parseInt(value || '0', 10) === 0)) {
        updated[scheduleId].scrap_reasons = [];
      }
      return updated;
    });
  };

  const getScrapReasons = (scheduleId) => {
    return itemInputs[scheduleId]?.scrap_reasons || [];
  };

  const getRemainingScrapQty = (scheduleId) => {
    const input = itemInputs[scheduleId] || {};
    const scrapped = parseInt(input.scrapped_quantity || '0', 10) || 0;
    const reasons = getScrapReasons(scheduleId);
    const allocated = reasons.reduce((sum, r) => sum + (parseInt(r.quantity || '0', 10) || 0), 0);
    return Math.max(0, scrapped - allocated);
  };

  const addScrapReason = (scheduleId) => {
    setItemInputs((prev) => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        scrap_reasons: [
          ...(prev[scheduleId]?.scrap_reasons || []),
          { reason_code: '', quantity: getRemainingScrapQty(scheduleId) || 1 },
        ],
      },
    }));
  };

  const updateScrapReason = (scheduleId, index, field, value) => {
    setItemInputs((prev) => {
      const reasons = [...(prev[scheduleId]?.scrap_reasons || [])];
      reasons[index] = { ...reasons[index], [field]: value };
      return {
        ...prev,
        [scheduleId]: {
          ...prev[scheduleId],
          scrap_reasons: reasons,
        },
      };
    });
  };

  const removeScrapReason = (scheduleId, index) => {
    setItemInputs((prev) => {
      const reasons = [...(prev[scheduleId]?.scrap_reasons || [])];
      reasons.splice(index, 1);
      return {
        ...prev,
        [scheduleId]: {
          ...prev[scheduleId],
          scrap_reasons: reasons,
        },
      };
    });
  };

  const handleConfirm = async () => {
    if (!selectedDay) return;
    const items = selectedDay.uncompletedItems.map((item) => {
      const input = itemInputs[item._id] || {};
      const scrappedQty = parseInt(input.scrapped_quantity || '0', 10) || 0;
      const scrapReasons = (input.scrap_reasons || [])
        .filter((r) => r.reason_code && (parseInt(r.quantity || '0', 10) > 0))
        .map((r) => {
          const reason = findScrapReason(r.reason_code);
          return {
            reason_code: r.reason_code,
            category: reason?.category || '',
            item: reason?.name || '',
            quantity: parseInt(r.quantity || '0', 10) || 0,
          };
        });
      return {
        schedule_id: item._id,
        completed_quantity: parseInt(input.completed_quantity || '0', 10) || 0,
        scrapped_quantity: scrappedQty,
        scrap_reasons: scrappedQty > 0 ? scrapReasons : [],
        notes: input.notes || '',
      };
    });

    setLoading(true);
    try {
      await onConfirm(selectedDate, items);
      setItemInputs({});
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            {singleDay && selectedDay
              ? format(selectedDay.date, 'yyyy年MM月dd日 结算', { locale: zhCN })
              : '日结算'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!singleDay && (
            <>
              <div className="text-sm text-gray-600">
                选择要结算的日期，填写每个任务的当日完成数量和报废数量。未完成的将自动加塞到次日。
              </div>

              {/* Date selector */}
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-1">
                {settleData.map((day) => (
                  <button
                    key={day.date.toISOString()}
                    onClick={() => handleSelectDate(day.date)}
                    className={`
                      p-2 rounded-md text-center text-xs transition-colors
                      ${isSameDay(day.date, selectedDate)
                        ? 'bg-blue-100 border border-blue-300 text-blue-800'
                        : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700'}
                    `}
                  >
                    <div className="font-semibold">{format(day.date, 'MM/dd')}</div>
                    <div className="text-[10px] mt-0.5">
                      {day.uncompleted > 0 ? (
                        <span className="text-amber-600">{day.uncompleted}待结</span>
                      ) : (
                        <span className="text-green-600">已结清</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {historicalLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              加载该日期排程数据...
            </div>
          ) : selectedDay ? (
            <div className="border rounded-md p-3 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">
                  {format(selectedDay.date, 'yyyy年MM月dd日', { locale: zhCN })}
                </span>
                <span className="text-xs text-gray-400">
                  共 {selectedDay.uncompletedItems.length} 个待结算任务
                </span>
              </div>

              {selectedDay.uncompleted > 0 ? (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                  {selectedDay.uncompletedItems.map((item) => {
                    const input = itemInputs[item._id] || {};
                    const prevCompleted = item.completed_quantity || 0;
                    const inputCompleted = parseInt(input.completed_quantity || '0', 10) || 0;
                    const inputScrapped = parseInt(input.scrapped_quantity || '0', 10) || 0;
                    // Scraps must be re-made, so they count against remaining work.
                    const remaining = item.assigned_quantity - prevCompleted - inputCompleted - inputScrapped;
                    const allDone = remaining <= 0;

                    return (
                      <div
                        key={item._id}
                        className={`text-xs bg-white rounded border p-2.5 ${allDone ? 'border-green-300 bg-green-50/30' : 'border-gray-200'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold text-gray-800">
                            {item.step_name}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {item.work_center_name} · {item.production_order_number}
                          </div>
                        </div>
                        <div className="text-[10px] text-gray-500 mb-2">
                          {item.finished_product_code} {item.finished_product_name && `· ${item.finished_product_name}`}
                          {' '}· 分配: <span className="font-medium text-gray-700">{item.assigned_quantity}</span>件
                          {item.completed_quantity > 0 && (
                            <span className="text-green-600 ml-1">
                              (之前已完成 {item.completed_quantity})
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[10px] text-gray-500">今日完成</Label>
                            <Input
                              type="number"
                              min="0"
                              className="h-7 text-xs mt-0.5"
                              value={input.completed_quantity || ''}
                              placeholder="0"
                              onChange={(e) => handleInputChange(item._id, 'completed_quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-gray-500">今日报废</Label>
                            <Input
                              type="number"
                              min="0"
                              className="h-7 text-xs mt-0.5"
                              value={input.scrapped_quantity || ''}
                              placeholder="0"
                              onChange={(e) => handleInputChange(item._id, 'scrapped_quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-gray-500">剩余</Label>
                            <div className={`h-7 flex items-center text-xs font-medium mt-0.5 px-2 rounded border ${allDone ? 'text-green-700 border-green-200 bg-green-50' : 'text-amber-700 border-amber-200 bg-amber-50'}`}>
                              {allDone ? '全部完成' : `${remaining} 件`}
                            </div>
                          </div>
                        </div>

                        <div className="mt-2">
                          <Input
                            className="h-7 text-xs"
                            placeholder="备注（可选）"
                            value={input.notes || ''}
                            onChange={(e) => handleInputChange(item._id, 'notes', e.target.value)}
                          />
                        </div>

                        {inputScrapped > 0 && (
                          <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-[10px] text-gray-500">报废原因明细</Label>
                              <span className={`text-[10px] ${getRemainingScrapQty(item._id) === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                                待分配: {getRemainingScrapQty(item._id)} 件
                              </span>
                            </div>
                            <div className="space-y-2">
                              {(getScrapReasons(item._id) || []).map((reason, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                                  <div className="col-span-8">
                                    <Select
                                      value={reason.reason_code || ''}
                                      onValueChange={(value) => updateScrapReason(item._id, idx, 'reason_code', value)}
                                    >
                                      <SelectTrigger className="h-7 text-xs">
                                        <SelectValue placeholder="选择报废原因" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {FLAT_SCRAP_REASONS.map((r) => (
                                          <SelectItem key={r.code} value={r.code} className="text-xs">
                                            {r.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="col-span-3">
                                    <Input
                                      type="number"
                                      min="1"
                                      max={inputScrapped}
                                      className="h-7 text-xs"
                                      value={reason.quantity || ''}
                                      placeholder="数量"
                                      onChange={(e) => updateScrapReason(item._id, idx, 'quantity', e.target.value)}
                                    />
                                  </div>
                                  <div className="col-span-1 flex justify-end">
                                    <button
                                      type="button"
                                      onClick={() => removeScrapReason(item._id, idx)}
                                      className="text-gray-400 hover:text-red-500"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {getRemainingScrapQty(item._id) > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="mt-2 h-7 text-xs px-2"
                                onClick={() => addScrapReason(item._id)}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                添加报废原因
                              </Button>
                            )}
                            {getRemainingScrapQty(item._id) !== 0 && (
                              <div className="mt-1 text-[10px] text-amber-600">
                                报废原因数量之和必须等于报废数量
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
                  当天所有任务已完成或已结算，无需操作。
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              !selectedDay ||
              selectedDay.uncompleted === 0 ||
              loading ||
              historicalLoading ||
              selectedDay.uncompletedItems.some((item) => {
                const input = itemInputs[item._id] || {};
                const scrapped = parseInt(input.scrapped_quantity || '0', 10) || 0;
                if (scrapped === 0) return false;
                return getRemainingScrapQty(item._id) !== 0;
              })
            }
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            确认结算
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DailySettleDialog;
